const DEFAULT_LOCALE = "de-CH";

// All date/time formatting is pinned to the site's timezone so the values are
// identical whether they render at build time (Netlify runs in UTC) or in the
// visitor's browser (Europe/Zurich). Without this, dates stored near midnight
// UTC (e.g. "2027-01-26T23:00:00+00:00" = Zurich midnight) format to a
// different day/time on each side and Vue reports a hydration mismatch.
export const SITE_TIME_ZONE = "Europe/Zurich";

/** Locale tag for Intl from an i18n locale code ("de" → "de-CH", "en" → "en-US"). */
export function intlLocale(locale: string): string {
  return locale === "en" ? "en-US" : DEFAULT_LOCALE;
}

/** Day/month/year as they fall in the site's timezone (not the runtime's). */
function zonedYmd(iso: string): { day: number; month: number; year: number } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SITE_TIME_ZONE,
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).formatToParts(new Date(iso));
  const val = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  return { day: val("day"), month: val("month"), year: val("year") };
}

/** Calendar year in the site's timezone (safe for hydration). */
export function zonedYear(iso: string | null): number | "" {
  return iso ? zonedYmd(iso).year : "";
}

/**
 * de: "27.–31. Januar 2027" (same month) or "27. Januar – 3. Februar 2027".
 * en: "January 27–31, 2027" (same month) or "January 27 – February 3, 2027".
 */
export function formatDateRange(
  startIso: string | null,
  endIso: string | null,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!startIso) return "";
  const monthName = (iso: string) =>
    new Intl.DateTimeFormat(locale, {
      month: "long",
      timeZone: SITE_TIME_ZONE,
    }).format(new Date(iso));
  const monthFirst = locale.startsWith("en");
  const s = zonedYmd(startIso);
  if (!endIso) {
    return monthFirst
      ? `${monthName(startIso)} ${s.day}, ${s.year}`
      : `${s.day}. ${monthName(startIso)} ${s.year}`;
  }
  const e = zonedYmd(endIso);
  const sameMonth = s.month === e.month && s.year === e.year;
  if (sameMonth) {
    return monthFirst
      ? `${monthName(startIso)} ${s.day}–${e.day}, ${e.year}`
      : `${s.day}.–${e.day}. ${monthName(startIso)} ${e.year}`;
  }
  return monthFirst
    ? `${monthName(startIso)} ${s.day} – ${monthName(endIso)} ${e.day}, ${e.year}`
    : `${s.day}. ${monthName(startIso)} – ${e.day}. ${monthName(endIso)} ${e.year}`;
}

/** "Donnerstag, 22. Januar" — program day label. */
export function formatWeekdayDate(
  iso: string | null,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat(locale, {
    timeZone: SITE_TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(iso));
}

/**
 * "Donnerstag 21.01.27" — localized weekday + Swiss numeric date (dd.mm.yy).
 * Used for the program day headings; the numeric part stays Swiss-dotted in
 * both locales.
 */
export function formatWeekdayDateNumeric(
  iso: string | null,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return "";
  const date = new Date(iso);
  const weekday = new Intl.DateTimeFormat(locale, {
    timeZone: SITE_TIME_ZONE,
    weekday: "long",
  }).format(date);
  const numeric = new Intl.DateTimeFormat("de-CH", {
    timeZone: SITE_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
  return `${weekday} ${numeric}`;
}

/** "19.30 Uhr" (de) / "7:30 PM" (en). */
export function formatTime(
  iso: string | null,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return "";
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: SITE_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: locale.startsWith("en"),
  }).format(new Date(iso));
  return locale.startsWith("de") ? `${time.replace(":", ".")} Uhr` : time;
}

/** Stable day key (YYYY-MM-DD) for grouping/filtering program items. */
export function dayKey(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

/**
 * Chronological copy of a list of dated items (date and time of day).
 * Items without a date keep their relative order and go last.
 */
export function sortByEventDate<T extends { eventDate: string | null }>(
  items: readonly T[],
): T[] {
  const time = (iso: string | null) =>
    iso ? new Date(iso).getTime() : Number.POSITIVE_INFINITY;
  return [...items].sort((a, b) => time(a.eventDate) - time(b.eventDate));
}
